import { useState, useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Camera, X, Wrench, FileText } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function QRScannerPage({ onLogout, userRole, isFeatureEnabled }) {
  const [scanning, setScanning] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [machine, setMachine] = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const navigate = useNavigate();

  const startScanning = async () => {
    try {
      setCameraError(null);
      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        onScanSuccess,
        onScanError
      );

      setScanning(true);
    } catch (err) {
      console.error("Camera error:", err);
      setCameraError("Unable to access camera. Please check permissions.");
      toast.error("Camera access denied");
    }
  };

  const stopScanning = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
    setScanning(false);
  };

  const onScanSuccess = async (decodedText) => {
    console.log("QR Code detected:", decodedText);
    
    // Stop scanning
    await stopScanning();
    
    try {
      // Parse QR code data
      const data = eval(`(${decodedText})`); // Parse Python dict string
      setScannedData(data);
      
      // Lookup machine by machine_number
      const identifier = data.machine_number || data.serial_number || data.asset_number;
      if (identifier) {
        const response = await axios.get(`${API}/machines/lookup/${identifier}`, {
          headers: getAuthHeaders(),
        });
        setMachine(response.data);
        toast.success(`Machine found: ${response.data.name}`);
      }
    } catch (error) {
      console.error("Lookup error:", error);
      toast.error("Machine not found or invalid QR code");
      setScannedData({ raw: decodedText });
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore continuous scanning errors
    if (!errorMessage.includes("NotFoundException")) {
      console.warn("Scan error:", errorMessage);
    }
  };

  const handleReset = () => {
    setScannedData(null);
    setMachine(null);
  };

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && scanning) {
        stopScanning();
      }
    };
  }, [scanning]);

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="qr-scanner-page">
        <div>
          <h1 className="font-heading font-black text-4xl text-[#0F172A] mb-2" data-testid="scanner-title">
            QR Code Scanner
          </h1>
          <p className="text-[#64748B] text-sm">Scan machine asset tags for instant lookup</p>
        </div>

        {!machine && (
          <div className="bg-white border border-[#E2E8F0] p-6">
            <div className="mb-6">
              <h2 className="font-heading font-bold text-lg text-[#0F172A] mb-2">
                Camera Scanner
              </h2>
              <p className="text-[#64748B] text-sm">
                Position QR code within the frame to scan automatically
              </p>
            </div>

            {!scanning && !scannedData && (
              <div className="text-center py-12">
                <Camera size={64} className="mx-auto text-[#64748B] mb-6" />
                <Button
                  onClick={startScanning}
                  className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none"
                  data-testid="start-scan-button"
                >
                  <Camera size={16} className="mr-2" />
                  Start Camera
                </Button>
                {cameraError && (
                  <div className="mt-4 text-[#DC2626] text-sm">
                    {cameraError}
                  </div>
                )}
              </div>
            )}

            {scanning && (
              <div>
                <div 
                  id="qr-reader" 
                  className="w-full max-w-md mx-auto border-2 border-[#2563EB] rounded-sm overflow-hidden"
                  data-testid="qr-reader"
                ></div>
                <div className="text-center mt-4">
                  <Button
                    onClick={stopScanning}
                    variant="outline"
                    className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                    data-testid="stop-scan-button"
                  >
                    <X size={16} className="mr-2" />
                    Stop Scanning
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm text-[#64748B]">
                  <p>Point your camera at a QR code asset tag</p>
                  <p className="mt-1">Scanning will stop automatically when detected</p>
                </div>
              </div>
            )}
          </div>
        )}

        {machine && (
          <div className="bg-white border border-[#E2E8F0] p-6" data-testid="machine-details">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="font-heading font-bold text-2xl text-[#0F172A] mb-2">
                  Machine Found
                </h2>
                <p className="text-[#64748B] text-sm">Scanned successfully</p>
              </div>
              <Button
                onClick={handleReset}
                variant="outline"
                className="rounded-none border-[#E2E8F0]"
                data-testid="scan-another-button"
              >
                Scan Another
              </Button>
            </div>

            <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-6 space-y-4">
              <div className="flex items-start gap-4">
                <Wrench size={48} className="text-[#2563EB] flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-mono text-2xl font-bold text-[#2563EB] mb-2">
                    {machine.machine_number}
                  </div>
                  <div className="text-xl text-[#0F172A] mb-3">
                    {machine.name}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {machine.serial_number && (
                      <div>
                        <span className="text-[#64748B]">Serial Number:</span>
                        <div className="font-mono text-[#0F172A] mt-1">{machine.serial_number}</div>
                      </div>
                    )}
                    {machine.asset_number && (
                      <div>
                        <span className="text-[#64748B]">Asset Number:</span>
                        <div className="font-mono text-[#0F172A] mt-1">{machine.asset_number}</div>
                      </div>
                    )}
                    <div>
                      <span className="text-[#64748B]">Type:</span>
                      <div className="text-[#0F172A] font-medium mt-1">{machine.type}</div>
                    </div>
                    {machine.location && (
                      <div>
                        <span className="text-[#64748B]">Location:</span>
                        <div className="text-[#0F172A] mt-1">{machine.location}</div>
                      </div>
                    )}
                  </div>

                  {machine.description && (
                    <div className="mt-4 pt-4 border-t border-[#E2E8F0]">
                      <span className="text-[#64748B] text-xs">Description:</span>
                      <p className="text-[#0F172A] text-sm mt-1">{machine.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                onClick={() => navigate("/machines")}
                className="flex-1 bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none"
                data-testid="view-all-machines-button"
              >
                <Wrench size={16} className="mr-2" />
                View All Machines
              </Button>
              <Button
                onClick={() => navigate("/blueprints")}
                variant="outline"
                className="flex-1 rounded-none border-[#E2E8F0]"
                data-testid="view-blueprints-button"
              >
                <FileText size={16} className="mr-2" />
                View Blueprints
              </Button>
            </div>
          </div>
        )}

        <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-6">
          <div className="flex items-start gap-3">
            <div className="text-[#2563EB] mt-1">💡</div>
            <div className="text-sm text-[#64748B]">
              <strong className="text-[#0F172A]">Scanner Tips:</strong>
              <ul className="mt-2 space-y-1 ml-4 list-disc">
                <li>Ensure good lighting for best results</li>
                <li>Hold camera steady and keep QR code in frame</li>
                <li>Scanner works with asset tags printed from this system</li>
                <li>Mobile devices: Use rear camera for better quality</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
